import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { Spinner } from "@fluentui/react-components";

/* global document, Office, module, require */

const rootElement = document.getElementById("container");
const root = createRoot(rootElement);

// Render a simple loading spinner initially.
root.render(<Spinner label="Initializing..." />);

/**
 * =========================================================================
 *  The Core Application Logic and Authentication Controller
 * =========================================================================
 */
const AuthController = () => {
  const [iapToken, setIapToken] = React.useState(null);
  const [authError, setAuthError] = React.useState(null);
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);

  // This check is now reliable because it's only called after initialization.
  const isOfficeEnvironment = () => typeof Office !== "undefined" && Office.context && Office.context.ui;

  const processAuthToken = (event) => {
    const messageString = event.message || event.data;

    if (window.loginDialog) {
      // In some browser cases, the dialog might close itself.
      try {
        window.loginDialog.close();
      } catch (e) {
        console.warn("Dialog was already closed.");
      }
    }

    try {
      const message = JSON.parse(messageString);
      if (message.status === "success" && message.token) {
        console.log("Authentication successful! Storing IAP token.");
        setIapToken(message.token);
      } else {
        throw new Error(message.error || "Authentication failed.");
      }
    } catch (e) {
      console.error("Failed to process auth token:", e);
      setAuthError(e.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  const startLoginFlow = () => {
    console.log("Login button clicked. Starting dialog flow...");
    setIsAuthenticating(true);
    setAuthError(null);

    const authUrl = location.origin + '/auth-start.html';

    if (isOfficeEnvironment()) {
      // --- OFFICE ADD-IN LOGIC ---
      console.log("Running in Office. Using displayDialogAsync().");
      Office.context.ui.displayDialogAsync(authUrl, { height: 75, width: 50, displayInIframe: true }, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Dialog failed to open: " + asyncResult.error.message);
          setAuthError("Could not open authentication window.");
          setIsAuthenticating(false);
          return;
        }
        window.loginDialog = asyncResult.value;
        window.loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processAuthToken);
      });
    } else {
      // --- BROWSER FALLBACK LOGIC ---
      console.log("Running in browser fallback. Using window.open().");
      const dialogWidth = 800;
      const dialogHeight = 600;
      window.loginDialog = window.open(authUrl, 'auth_dialog', `width=${dialogWidth},height=${dialogHeight}`);
      window.addEventListener("message", processAuthToken, { once: true });
    }
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <App
        title="Contoso Task Pane Add-in"
        onLoginRequest={startLoginFlow}
          initialIapToken={iapToken}
          isAuthenticating={isAuthenticating}
          authError={authError}
      />
    </FluentProvider>
  );
};


/**
 * =========================================================================
 *  NEW ROBUST INITIALIZER
 *  This function patiently waits for Office.js to load.
 * =========================================================================
 */
function initialize() {
  // The function that renders the main React application.
  const renderApp = () => {
    root.render(<AuthController />);
  };

  // Check if Office is available.
  if (typeof Office !== "undefined") {
    // It is, so we can use the official Office.onReady hook.
    console.log("Office.js detected. Using Office.onReady().");
    Office.onReady(renderApp);
  } else {
    // Office is not available. This is a standard browser environment.
    console.log("Office.js not detected. Running in browser mode.");
    // Wait for the DOM to be ready before rendering.
    document.addEventListener("DOMContentLoaded", renderApp);
  }
}

// Start the initialization process.
initialize();


// --- Webpack Hot Module Replacement (leave as is) ---
if (module.hot) {
  module.hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}

